/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

export type Primitive = string | number | boolean | undefined | null;

export interface UrlStateOptions<T extends Record<string, Primitive>> {
  defaults?: Partial<T>;
  replace?: boolean;
  enabled?: boolean;
}

export function useUrlState<T extends Record<string, Primitive>>(
  keys: (keyof T)[],
  options?: UrlStateOptions<T>,
) {
  const { defaults, replace = true, enabled = true } = options ?? {};
  const [hasMounted, setHasMounted] = useState(false);
  const [state, setState] = useState<Partial<T>>(() => {
    if (!enabled || typeof window === "undefined") {
      return defaults ? { ...defaults } : {};
    }
    const params = new URLSearchParams(window.location.search);
    const initial: Partial<T> = {};
    const parseValue = (raw: string): Primitive => {
      if (raw === "true" || raw === "false") return raw === "true";
      if (!Number.isNaN(Number(raw)) && raw.trim() !== "") return Number(raw);
      return raw;
    };
    for (const key of keys) {
      const raw = params.get(String(key));
      if (raw != null) {
        // best effort cast for numbers/booleans/strings
        const k = key as keyof T;
        (initial as Partial<T>)[k] = parseValue(raw) as T[typeof k];
      } else if (defaults && key in defaults) {
        const k = key as keyof T;
        (initial as Partial<T>)[k] = defaults[k] as T[typeof k];
      }
    }
    return initial;
  });

  // Hydration effect
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const setUrlState = useCallback((next: Partial<T>) => {
    setState((prev) => {
      return { ...prev, ...next } as Partial<T>;
    });
  }, []);

  // Separate effect to handle URL updates
  // Uses history.replaceState/pushState instead of router.replace/push to avoid
  // triggering Next.js RSC server round-trips on every filter/page change.
  // This keeps URL in sync for bookmarkability without causing server re-renders,
  // Suspense boundary triggers, or client component unmount/remount cycles.
  useEffect(() => {
    if (!enabled || typeof window === "undefined" || !hasMounted) {
      return;
    }

    const url = new URL(window.location.href);
    let hasChanges = false;

    for (const key of keys) {
      const k = key as keyof T;
      const value = state[k];
      const currentValue = url.searchParams.get(String(key));

      if (value === undefined || value === null || value === "") {
        if (currentValue !== null) {
          url.searchParams.delete(String(key));
          hasChanges = true;
        }
      } else {
        const stringValue = String(value);
        if (currentValue !== stringValue) {
          url.searchParams.set(String(key), stringValue);
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      const newUrl = `${window.location.pathname}${url.search}`;
      if (replace) {
        window.history.replaceState(window.history.state, "", newUrl);
      } else {
        window.history.pushState(window.history.state, "", newUrl);
      }
    }
  }, [enabled, keys, replace, hasMounted, state]);

  // Sync when user navigates backwards/forwards
  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }
    const onPopState = () => {
      const params = new URLSearchParams(window.location.search);
      const next: Partial<T> = {};
      const parseValue = (raw: string | null): Primitive => {
        if (raw == null) return undefined;
        if (raw === "true" || raw === "false") return raw === "true";
        if (!Number.isNaN(Number(raw)) && raw.trim() !== "") return Number(raw);
        return raw;
      };
      for (const key of keys) {
        const k = key as keyof T;
        const raw = params.get(String(key));
        const parsed = parseValue(raw);
        // Only include keys that are present in URL or have defaults
        // This prevents resetting values that aren't in the URL
        if (parsed !== undefined || (defaults && key in defaults)) {
          (next as Partial<T>)[k] = (
            parsed !== undefined ? parsed : defaults?.[k]
          ) as T[typeof k];
        }
      }
      // Merge with current state to preserve values not in URL
      setState((prev) => ({ ...prev, ...next }) as Partial<T>);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enabled, keys, defaults]);

  return useMemo(() => ({ state, setUrlState }), [state, setUrlState]);
}
